import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';
import { cn } from './utils';

export interface TooltipProps {
  readonly content: ReactNode;
  readonly children: ReactNode;
  readonly side?: 'top' | 'right' | 'bottom' | 'left';
  readonly delay?: number;
}

/**
 * Dica de contexto.
 *
 * REGRA DO PROJETO: tooltip NUNCA é o único lugar onde uma informação existe.
 * Ele é dica suplementar. Motivo: em toque não há hover, e conteúdo essencial
 * escondido atrás de hover viola WCAG 1.3.1 e 1.4.13. Onde a informação é
 * necessária (o significado de uma flag de permissão, por exemplo), ela também
 * aparece como texto ou como `<Field description>`.
 *
 * O Radix expõe o conteúdo via `aria-describedby` no gatilho e o mantém aberto
 * ao focar por teclado — não só ao passar o mouse.
 */
export function Tooltip({ content, children, side = 'top', delay = 300 }: TooltipProps) {
  return (
    <TooltipPrimitive.Root delayDuration={delay}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            'z-tooltip max-w-xs rounded-md border border-border bg-surface-raised px-2.5 py-1.5',
            'text-xs text-content shadow-lg animate-content-in',
          )}
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-[var(--qc-color-surface-raised)]" width={10} height={5} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

/** Provedor único de tooltip. Montado uma vez no shell. */
export function TooltipProvider({ children }: { readonly children: ReactNode }) {
  return (
    <TooltipPrimitive.Provider delayDuration={300} skipDelayDuration={200}>
      {children}
    </TooltipPrimitive.Provider>
  );
}
